import React from 'react';
import {
    Autocomplete,
    CircularProgress,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import {subscribeToAllReports} from '../../../firebase/firestore/reports';
import {isIirProductType} from '../../products/productTypes';
import ProductTypeIcon from '../../products/ProductTypeIcon';
import UserAvatar from '../../../components/UserAvatar';

const GROUP_ORDER = ['Pitch', 'FINTEL', 'Drafts', 'Disclosures', 'SITREPs'];
const EMPTY_EXCLUDE_IDS = [];

function productTypeValue(productType) {
    return typeof productType === 'string' ? productType : productType?.value;
}

/**
 * Resolve Autocomplete group label to match Products-tab buckets.
 * Drafts (status !== approved) stay under "Drafts" even when productType is analytic.
 */
export function resolveProductGroup(report) {
    const type = productTypeValue(report?.productType);
    const status = String(report?.status || '').toLowerCase();

    if (type === 'disclosure' || type === 'ndp1_disclosure') {
        return 'Disclosures';
    }
    if (status !== 'approved') {
        return 'Drafts';
    }
    if (type === 'pitch') {
        return 'Pitch';
    }
    if (type === 'tactical_sitrep' || type === 'strategic_sitrep') {
        return 'SITREPs';
    }
    return 'FINTEL';
}

export function mapReportToOption(report) {
    const type = productTypeValue(report?.productType) || null;
    const ownerId = report?.owner || report?.userId || null;
    const ownerProfile = ownerId ? report?.authorProfiles?.[ownerId] : null;
    const ownerDisplayName = ownerProfile?.displayName
        || (ownerId && report?.authorDisplayNames ? report.authorDisplayNames[ownerId] : null)
        || null;
    return {
        id: report.id,
        title: report.title || report.name || report.id,
        productType: type,
        group: resolveProductGroup(report),
        ownerId,
        ownerDisplayName,
        ownerHasAvatar: ownerProfile?.hasAvatar === true,
    };
}

function sortOptions(rows) {
    return [...rows].sort((a, b) => {
        const ga = GROUP_ORDER.indexOf(a.group);
        const gb = GROUP_ORDER.indexOf(b.group);
        if (ga !== gb) return ga - gb;
        return String(a.title).localeCompare(String(b.title));
    });
}

function filterOptions(options, query) {
    const trimmedQuery = String(query || '').trim().toLowerCase();
    if (!trimmedQuery) return options;
    return options.filter((option) => {
        const title = String(option.title || '').toLowerCase();
        const id = String(option.id || '').toLowerCase();
        return title.includes(trimmedQuery) || id.includes(trimmedQuery);
    });
}

export default function ChartProductPicker({
    active = false,
    excludeIds = EMPTY_EXCLUDE_IDS,
    onSelect,
    onCancel,
    saving = false,
}) {
    const [options, setOptions] = React.useState([]);
    const [loading, setLoading] = React.useState(false);
    const [loadError, setLoadError] = React.useState('');

    const excludedKey = excludeIds.join(',');

    React.useEffect(() => {
        if (!active) {
            setLoadError('');
            return undefined;
        }

        const excluded = new Set(excludeIds);
        setLoading(true);
        setLoadError('');
        const unsubscribe = subscribeToAllReports(
            (data) => {
                const rows = sortOptions(
                    (Array.isArray(data) ? data : [])
                        .filter((report) => report?.id && !isIirProductType(report?.productType))
                        .filter((report) => !excluded.has(report.id))
                        .map(mapReportToOption),
                );

                setOptions(rows);
                setLoading(false);
            },
            (err) => {
                setLoadError(err?.message || 'Failed to load products.');
                setLoading(false);
            },
        );

        return () => {
            try {
                unsubscribe?.();
            } catch {
                // cleanup
            }
        };
    }, [active, excludedKey, excludeIds]);

    const handleChange = (_event, value) => {
        if (saving || !value) return;
        onSelect?.(value);
    };

    const handleKeyDown = (event) => {
        if (event.key === 'Escape' && !saving) {
            event.preventDefault();
            event.stopPropagation();
            onCancel?.();
        }
    };

    if (!active) return null;

    return (
        <Autocomplete
            openOnFocus
            autoHighlight
            disablePortal
            size="small"
            fullWidth
            options={options}
            groupBy={(option) => option.group || 'FINTEL'}
            getOptionLabel={(option) => option.title || option.id || ''}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            loading={loading}
            disabled={saving}
            filterOptions={(opts, {inputValue}) => filterOptions(opts, inputValue)}
            onChange={handleChange}
            noOptionsText={loadError || 'No products found.'}
            renderInput={(params) => (
                <TextField
                    {...params}
                    autoFocus
                    label="Product"
                    placeholder="Search by title or id"
                    error={Boolean(loadError)}
                    helperText={loadError || undefined}
                    onKeyDown={handleKeyDown}
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <>
                                {loading || saving ? (
                                    <CircularProgress color="inherit" size={14}/>
                                ) : null}
                                {params.InputProps.endAdornment}
                            </>
                        ),
                    }}
                />
            )}
            renderOption={(props, option) => {
                const {key, ...liProps} = props;
                const ownerUser = option.ownerId
                    ? {userId: option.ownerId, displayName: option.ownerDisplayName, hasAvatar: option.ownerHasAvatar}
                    : null;
                return (
                    <li key={key} {...liProps} style={{...liProps.style, paddingTop: 6, paddingBottom: 6}}>
                        <Stack spacing={0.25} sx={{width: '100%', minWidth: 0}}>
                            <Stack direction="row" spacing={0.75} alignItems="center">
                                <ProductTypeIcon productType={option.productType} sx={{ml: 0, flexShrink: 0}}/>
                                <Typography variant="body2" sx={{whiteSpace: 'normal', wordBreak: 'break-word', flex: 1}}>
                                    {option.title}
                                </Typography>
                            </Stack>
                            {ownerUser && (
                                <Stack direction="row" spacing={0.5} alignItems="center" sx={{pl: '22px'}}>
                                    <UserAvatar
                                        user={ownerUser}
                                        userId={option.ownerId}
                                        sx={{width: 14, height: 14, fontSize: 8}}
                                    />
                                    <Typography variant="caption" color="text.secondary">
                                        {option.ownerDisplayName || option.ownerId}
                                    </Typography>
                                </Stack>
                            )}
                        </Stack>
                    </li>
                );
            }}
            slotProps={{
                popper: {
                    placement: 'bottom-start',
                    sx: {zIndex: (theme) => theme.zIndex.modal + 1},
                },
            }}
        />
    );
}
